import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Clock, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AttendanceCodeGenerator({ open, onOpenChange, getAuthHeaders }) {
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [usedByCount, setUsedByCount] = useState(0);
  const [codeId, setCodeId] = useState(null);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/attendance/generate-code`, {}, getAuthHeaders());
      setCode(res.data.code);
      setExpiresAt(res.data.expires_at);
      setSecondsLeft(res.data.expires_in_seconds || 300);
      setCodeId(res.data.code_id);
      setUsedByCount(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar código');
      onOpenChange(false);
    } finally {
      setGenerating(false);
    }
  }, [getAuthHeaders, onOpenChange]);

  // Auto-generate on open
  useEffect(() => {
    if (open && !code) {
      generate();
    }
    if (!open) {
      // reset when closed
      setCode(null);
      setExpiresAt(null);
      setSecondsLeft(0);
      setUsedByCount(0);
      setCodeId(null);
    }
  }, [open, code, generate]);

  // Countdown
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const rem = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(rem);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [expiresAt]);

  // Poll used_by count every 3s to give feedback
  useEffect(() => {
    if (!codeId || !open) return;
    const iv = setInterval(async () => {
      try {
        // No specific endpoint yet; use attendance list as proxy could be heavy — skip for now.
        // Placeholder: keep 0 unless we build a dedicated GET /attendance/code/{id}.
      } catch { /* noop */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [codeId, open]);

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;
  const expired = secondsLeft <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="attendance-code-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-gold" />
            Código de Asistencia
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            El barbero debe teclear este código en su panel para registrar entrada o salida.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {generating || !code ? (
            <div className="text-center py-12 text-zinc-500">Generando código...</div>
          ) : (
            <>
              <div className="flex justify-center">
                <div
                  className={`px-8 py-6 rounded-lg border-2 ${expired ? 'border-red-500/50 bg-red-500/5' : 'border-gold/60 bg-gold/5'}`}
                  data-testid="attendance-code-value"
                >
                  <p className={`font-heading text-6xl tracking-[0.4em] text-center ${expired ? 'text-red-400' : 'text-gold'}`}>
                    {code}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 text-sm">
                <div className={`flex items-center gap-1.5 ${expired ? 'text-red-400' : secondsLeft < 60 ? 'text-yellow-400' : 'text-zinc-300'}`}>
                  <Clock className="w-4 h-4" />
                  <span data-testid="attendance-code-countdown">
                    {expired ? 'Vencido' : `${mmss}`}
                  </span>
                </div>
                {usedByCount > 0 && (
                  <div className="flex items-center gap-1.5 text-green-400">
                    <Users className="w-4 h-4" />
                    <span>{usedByCount} check-in{usedByCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              <div className="rounded-sm bg-zinc-800/50 border border-zinc-700/50 p-3 text-xs text-zinc-400 space-y-1">
                <p>• Válido por 5 minutos. Un mismo código sirve para varios barberos.</p>
                <p>• Solo funciona en esta sucursal.</p>
                <p>• Si vence, presiona &quot;Regenerar&quot; para crear uno nuevo.</p>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="attendance-code-close"
          >
            Cerrar
          </Button>
          <Button
            onClick={generate}
            disabled={generating}
            className="bg-gold text-black hover:bg-gold/90"
            data-testid="attendance-code-regenerate"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            Regenerar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
